import { ActionIcon, Button, Menu } from '@mantine/core';
import { ErrorBoundary } from '@sentry/react';
import {
  IconBrandWhatsapp,
  IconCalendar,
  IconCalendarPlus,
  IconChevronLeft,
  IconChevronRight,
  IconFilter,
  IconPlus,
  IconPrinter,
  IconQrcode,
  IconShare,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { AccessDenied } from 'src/Components/AccessDenied';
import { Filters } from 'src/Components/Calendars/Filters';
import { ListCalendar } from 'src/Components/Calendars/ListCalendar';
import { MonthCalendar } from 'src/Components/Calendars/MonthCalendar';
import { StaysTimeline } from 'src/Components/Calendars/StaysTimeline';
import { WeekCalendar } from 'src/Components/Calendars/WeekCalendar';
import { CalendarSyncModal } from 'src/Components/Modals/CalendarSyncModal';
import { EventFilterModal } from 'src/Components/Modals/EventFilterModal';
import { PrintOptionsModal } from 'src/Components/Modals/PrintOptionsModal';
import { QRCodeModal } from 'src/Components/Modals/QRCodeModal';
import { WhatsAppShareModal } from 'src/Components/Modals/WhatsAppShareModal';
import {
  DEFAULT_PRINT_OPTIONS,
  PrintFormat,
  PrintOptions,
} from 'src/Components/PrintTemplates/PrintablePlanning/PrintablePlanning.types';
import { HeaderActionIcon } from 'src/Components/Shared/HeaderActionIcon';
import { VideoTutorialButton } from 'src/Components/Shared/VideoTutorialButton';
import useFormatDateRange, {
  VIEW_MAPPINGS,
  ViewRange,
} from 'src/Hooks/CalendarLogic/useFormatDateRange';
import { useCreatePlanningExport } from 'src/Hooks/Mutations/useCreatePlanningExport';
import usePrintPlanning from 'src/Hooks/Printables/usePrintPlanning';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchEvents from 'src/Hooks/Queries/useFetchEvents';
import { useCanCreate, useCanRead } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { trackPlanningViewChanged } from 'src/Services/Mixpanel/events';
import { useEventFiltersStore } from 'src/Stores/eventFiltersStore';

import styles from './PlanningPage.module.scss';

const PlanningPage = () => {
  const { t } = useTranslation();

  // Permission checks
  const { hasPermission: canRead, ownerName } = useCanRead('event');
  const { hasPermission: canCreate } = useCanCreate('event');
  const { hasPermission: canExport } = useCanCreate('entityExport');

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const isMobile = useIsMobile(1350);
  const [activeView, setActiveView] = useState<ViewRange>('week');
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { assetId } = useParams<{ assetId: string }>();

  const formattedDateRange = useFormatDateRange({
    date: selectedDate,
    rangeType: activeView,
    compact: isMobile ?? false,
  });

  const handleViewChange = useCallback((view: ViewRange) => {
    setActiveView(view);
    trackPlanningViewChanged({ view });
  }, []);

  const changeDate = useCallback(
    (direction: 'prev' | 'next') => {
      setSelectedDate(date => {
        const dateUnit = VIEW_MAPPINGS[activeView];
        return direction === 'next' ? date.add(1, dateUnit) : date.subtract(1, dateUnit);
      });
    },
    [activeView]
  );

  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [showCalendarSyncModal, setShowCalendarSyncModal] = useState(false);
  const [showPrintOptionsModal, setShowPrintOptionsModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [exportUrl, setExportUrl] = useState<string | undefined>(undefined);
  const [icalUrl, setIcalUrl] = useState<string | undefined>(undefined);
  const [exportName, setExportName] = useState<string | undefined>(undefined);
  const pendingActionRef = useRef<'qrcode' | 'calendar' | 'print' | 'whatsapp' | null>(null);
  const pendingPrintOptionsRef = useRef<PrintOptions>(DEFAULT_PRINT_OPTIONS);
  const pendingPrintFormatRef = useRef<PrintFormat>('grid');

  const { data: asset } = useFetchAssetDetails();
  const { printPlanning } = usePrintPlanning();
  const selectedEventTypes = useEventFiltersStore(state => state.selectedEventTypes);
  const showPracticalReminders = useEventFiltersStore(state => state.showPracticalReminders);

  const { data: events } = useFetchEvents({
    startDate: selectedDate.startOf('month').add(-1, 'week'),
    endDate: selectedDate.endOf('month').add(1, 'week'),
  });

  const { mutate: exportPlanning, isLoading: isExporting } = useCreatePlanningExport({
    onSuccess: data => {
      setExportUrl(data.publicUrl);

      // Extract the token from the publicUrl (format: .../export/{token})
      const token = data.publicUrl.split('/export/').pop();
      if (token) {
        const apiUrl = import.meta.env.VITE_API_URL;
        setIcalUrl(`${apiUrl}/public/exports/${token}/calendar.ics`);
      }

      // Open the appropriate modal based on the pending action
      if (pendingActionRef.current === 'qrcode') {
        setShowQRCodeModal(true);
      } else if (pendingActionRef.current === 'calendar') {
        setShowCalendarSyncModal(true);
      } else if (pendingActionRef.current === 'whatsapp') {
        setShowWhatsAppModal(true);
      } else if (pendingActionRef.current === 'print') {
        printPlanning({
          events: events ?? [],
          selectedDate,
          view: activeView === 'week' ? 'week' : 'month',
          assetName: asset?.name ?? '',
          exportUrl: data.publicUrl,
          activeEventTypes: selectedEventTypes,
          printOptions: pendingPrintOptionsRef.current,
          format: pendingPrintFormatRef.current,
        });
      }
      pendingActionRef.current = null;
    },
  });

  const onQRCodeClick = useCallback(() => {
    setExportName(t('planning.exportName', 'Calendrier'));
    pendingActionRef.current = 'qrcode';
    exportPlanning();
  }, [exportPlanning, t]);

  const onCalendarSyncClick = useCallback(() => {
    pendingActionRef.current = 'calendar';
    exportPlanning();
  }, [exportPlanning]);

  const onWhatsAppClick = useCallback(() => {
    pendingActionRef.current = 'whatsapp';
    exportPlanning();
  }, [exportPlanning]);

  const onPrintClick = useCallback(() => {
    setShowPrintOptionsModal(true);
  }, []);

  const onPrintConfirm = useCallback(
    ({ options, format }: { options: PrintOptions; format: PrintFormat }) => {
      pendingPrintOptionsRef.current = options;
      pendingPrintFormatRef.current = format;
      pendingActionRef.current = 'print';
      setShowPrintOptionsModal(false);
      exportPlanning();
    },
    [exportPlanning]
  );

  const handleStaysEmptyCellClick = useCallback(
    ({ householdId, date }: { householdId: string; date: Date }) => {
      navigate('new-event', {
        state: { defaultDate: date.getTime(), defaultHouseholdId: householdId },
      });
    },
    [navigate]
  );

  const assetAboveQuotas = useIsAssetAboveQuotas();

  const handleDateClick = useCallback(
    (date: Date) => {
      const dateStr = dayjs(date).format('YYYY-MM-DD');
      navigate(`day/${dateStr}`);
    },
    [navigate]
  );

  // Show access denied if user doesn't have read permission
  if (!canRead) {
    return <AccessDenied ownerName={ownerName} />;
  }

  return (
    <div className={styles.container}>
      <QRCodeModal
        open={showQRCodeModal}
        url={exportUrl ?? ''}
        onClose={() => setShowQRCodeModal(false)}
        exportName={exportName ?? ''}
      />
      <CalendarSyncModal
        open={showCalendarSyncModal}
        onClose={() => setShowCalendarSyncModal(false)}
        icalUrl={icalUrl ?? ''}
      />
      <WhatsAppShareModal
        open={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        assetName={asset?.name ?? ''}
        subscribeUrl={exportUrl ? `${exportUrl}/abonnement` : ''}
      />
      <PrintOptionsModal
        open={showPrintOptionsModal}
        onClose={() => setShowPrintOptionsModal(false)}
        onConfirm={onPrintConfirm}
        loading={isExporting}
        defaultFormat={activeView === 'stays' ? 'stays' : 'grid'}
      />
      <div className={styles.leftPart}>
        {canCreate && (
          <div className={styles.addEventButtonContainer}>
            <Button
              className={styles.addEventButton}
              onClick={() => navigate('new-event')}
              leftSection={<IconPlus size={20} />}
              disabled={assetAboveQuotas}
            >
              {t('planning.newEvent', 'Nouvel événement')}
            </Button>
          </div>
        )}
        <div className={styles.filterContainer}>
          <div className={styles.title}>{t('planning.filters.title', 'Filtres')}</div>
          <Filters />
        </div>
      </div>
      <div className={styles.columnContainer}>
        <div className={styles.topLine}>
          <div className={styles.dateSelectionLine}>
            <ActionIcon variant="subtle" size="xl" onClick={() => changeDate('prev')}>
              <IconChevronLeft size={24} className={styles.chevronIcon} />
            </ActionIcon>
            <ActionIcon variant="subtle" size="xl" onClick={() => changeDate('next')}>
              <IconChevronRight size={24} className={styles.chevronIcon} />
            </ActionIcon>
            <div className={styles.currentDate}>{formattedDateRange}</div>
            {isMobile && canCreate && (
              <ActionIcon
                radius={'xl'}
                size={'lg'}
                onClick={() => navigate('new-event')}
                disabled={assetAboveQuotas}
              >
                <IconPlus size={20} />
              </ActionIcon>
            )}
          </div>
          <div className={styles.buttons}>
            {isMobile ? (
              <div className={styles.mobileActionButtons}>
                <ActionIcon
                  radius={'xl'}
                  size={'lg'}
                  onClick={() => setFilterModalOpen(true)}
                  variant={'outline'}
                >
                  <IconFilter size={20} />
                </ActionIcon>
                {canExport && (
                  <Menu shadow="md" width={250} position="bottom-end">
                    <Menu.Target>
                      <ActionIcon radius={'xl'} size={'lg'} variant={'outline'}>
                        <IconShare size={20} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item leftSection={<IconQrcode size={18} />} onClick={onQRCodeClick}>
                        {t('planning.exportQRCode', 'Partager via QR Code')}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconCalendar size={18} />}
                        onClick={onCalendarSyncClick}
                      >
                        {t('planning.exportCalendar', 'Synchroniser avec un calendrier')}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconBrandWhatsapp size={18} />}
                        onClick={onWhatsAppClick}
                      >
                        {t('planning.actions.whatsapp', 'Envoyer à la famille')}
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </div>
            ) : (
              canExport && (
                <>
                  <HeaderActionIcon
                    icon={IconPrinter}
                    label={t('planning.actions.print', 'Imprimer')}
                    onClick={onPrintClick}
                    disabled={isExporting}
                  />
                  <HeaderActionIcon
                    icon={IconQrcode}
                    label={t('planning.actions.qrCode', 'Partager par QR code')}
                    onClick={onQRCodeClick}
                    disabled={isExporting}
                  />
                  <HeaderActionIcon
                    icon={IconCalendarPlus}
                    label={t('planning.actions.calendarSync', 'Ajouter à mon agenda')}
                    onClick={onCalendarSyncClick}
                    disabled={isExporting}
                  />
                  <HeaderActionIcon
                    icon={IconBrandWhatsapp}
                    label={t('planning.actions.whatsapp', 'Envoyer à la famille')}
                    onClick={onWhatsAppClick}
                    disabled={isExporting}
                    variant="whatsapp"
                  />
                </>
              )
            )}
            <VideoTutorialButton videoUrl={'https://youtu.be/24yyA9vR6b0'} />
            <Button.Group>
              <Button data-active={activeView === 'list'} onClick={() => handleViewChange('list')}>
                {t('planning.list', 'Liste')}
              </Button>
              <Button
                data-active={activeView === 'month'}
                onClick={() => handleViewChange('month')}
              >
                {t('planning.month', 'Mois')}
              </Button>
              <Button data-active={activeView === 'week'} onClick={() => handleViewChange('week')}>
                {t('planning.week', 'Semaine')}
              </Button>
              {!isMobile && (
                <Button
                  data-active={activeView === 'stays'}
                  onClick={() => handleViewChange('stays')}
                >
                  {t('planning.stays.tabLabel', 'Séjours')}
                </Button>
              )}
            </Button.Group>
          </div>
        </div>

        {activeView === 'list' && (
          <ListCalendar
            selectedDate={selectedDate}
            events={events}
            onDateClick={handleDateClick}
            showPracticalReminders={showPracticalReminders}
          />
        )}
        {activeView === 'month' && (
          <MonthCalendar
            selectedDate={selectedDate}
            events={events}
            onDateClick={handleDateClick}
            showPracticalReminders={showPracticalReminders}
          />
        )}
        {activeView === 'week' && (
          <WeekCalendar selectedDate={selectedDate} onDateClick={handleDateClick} />
        )}
        {activeView === 'stays' && (
          <StaysTimeline
            selectedDate={selectedDate}
            events={events}
            onEmptyCellClick={handleStaysEmptyCellClick}
          />
        )}
      </div>
      <ErrorBoundary
        key={location.pathname}
        fallback={({ resetError }) => (
          <div className={styles.itemErrorFallback} role="alert">
            <p>{t('planning.itemError.message', 'Impossible d’ouvrir cet élément.')}</p>
            <Button
              size="md"
              variant="outline"
              onClick={() => {
                resetError();
                if (assetId) {
                  navigate(`/asset/${assetId}/planning`, { replace: true });
                }
              }}
            >
              {t('planning.itemError.close', 'Fermer')}
            </Button>
          </div>
        )}
        onError={error => console.error(error)}
      >
        <Outlet />
      </ErrorBoundary>
      <EventFilterModal open={filterModalOpen} onClose={() => setFilterModalOpen(false)} />
    </div>
  );
};

export default PlanningPage;
